<template>
    <div class="step">
        <h2>When will camp run?</h2>
        <day-picker class="day-picker w-100"
                    label="Days"
                    height="48px"
                    v-model="weekDays">
        </day-picker>
        <div class="position-relative">
            <div v-if="formValid && !selectedMoreThenOneDay"
                 class="error">You have to enter at least one day</div>
        </div>
        <div class="d-flex justify-content-between">
            <date-picker label="Start Date"
                         id="start-date"
                         v-validate="{required: true, 'current-future-date': !$route.params.id}"
                         v-model="campStartDate">
            </date-picker>
            <div class="delimetr">-</div>
            <date-picker label="End Date"
                         id="end-date"
                         v-validate="{required: true, 'future-date': !$route.params.id}"
                         v-model="campEndDate">
            </date-picker>
        </div>
        <div class="position-relative">
            <div v-if="(!$route.params.id || isCampFormDraft) && campStartDate && currentOrFutureDate(campStartDate)"
                 class="error">
                Should be current or future date
            </div>
        </div>
        <div class="position-relative">
            <div v-if="campEndDateError"
                 class="error right-error">{{ campEndDateError }}</div>
        </div>
        <date-picker label="No camp dates"
                     class="no-class-dates"
                     id="no-camp-dates"
                     info="Enter dates that camp will not meet. e.g. holidays"
                     :chip-width="115"
                     v-model="noCampDates"
                     :multiple-select="true">
        </date-picker>
        <h2>When can parents register?</h2>
        <div class="row">
            <div class="col-12 col-sm-6">
                <date-picker label="Registration Opens"
                             id="sign-up-start-date"
                             class="sign-up-start-date"
                             info="Select when registration opens for this camp"
                             v-validate="{required: true, 'current-future-date': !$route.params.id}"
                             v-model="signUpBeginsDate">
                </date-picker>
            </div>
            <div class="col-12 col-sm-6">
                <time-picker id="sign-up-start-time"
                             class="mt-20"
                             v-model="signUpBeginsTime"
                             v-validate="'required'">
                </time-picker>
            </div>
        </div>
        <div class="position-relative">
            <div v-if="(!$route.params.id || isCampFormDraft) && signUpBeginsDate && currentOrFutureDate(signUpBeginsDate)"
                 class="error">Should be current or future date</div>
        </div>
        <div class="row">
            <div class="col-12 col-sm-6">
                <date-picker id="sign-up-end-date"
                             class="sign-up-end-date"
                             label="Registration Deadline"
                             info="Enter registration cutoff date and time"
                             v-validate="{required: true, 'future-date': !$route.params.id}"
                             v-model="signUpEndsDate">
                </date-picker>
                <div v-if="(!$route.params.id || isCampFormDraft) && signUpEndsDate && futureDate(signUpEndsDate)"
                         class="error registration-deadline-error">Should be future date</div>
                    <div v-if="signUpBeginsDate && signUpEndsDate && !signUpEndsValid && formValid"
                         class="error registration-deadline-error">The end date should be after start date</div>
            </div>
            <div class="col-12 col-sm-6">
                <time-picker id="sign-up-end-time"
                             class="mt-20"
                             v-model="signUpEndsTime"
                             v-validate="'required'">
                </time-picker>
            </div>
        </div>
        <div class="position-relative">
            <div v-if="signUpEndsDate && signUpBeginsDate && !signUpEndsValid && formValid"
                 class="error">The end date should be after start date</div>
        </div>
    </div>
</template>

<script>
    import {mapGetters, mapMutations} from 'vuex';
    import * as Mutations from '../../../../store/modules/camp-form/mutations';
    import {FormMixin} from '../../../../mixins/FormMixin';
    import moment from 'moment';

    export default {
        name: 'Step3',
        components: {
            DatePicker: () => import(/* webpackChunkName: 'camps' */ '../../../controls/DatePicker'),
            DayPicker:  () => import(/* webpackChunkName: 'camps' */ '../../../controls/DayPicker'),
            TimePicker: () => import(/* webpackChunkName: 'camps' */ '../../../controls/TimePicker'),
            Choice:     () => import(/* webpackChunkName: 'camps' */ '../../../controls/Choice'),
        },
        mixins: [FormMixin],
        watch: {
            stepValid: {
                handler(value) {
                    this.setValid(value);
                },
                immediate: true
            }
        },
        computed: {
            ...mapGetters({
                form: 'campCreationForm',
                duplicate: 'duplicateCampForm',
                isCampFormDraft: 'isCampFormDraft'
            }),
            stepValid() {
                return this.formValid && this.signUpEndsValid && this.campEndsValid && this.selectedMoreThenOneDay;
            },
            signUpEndsValid() {
                return this.signUpBeginsDate <= this.signUpEndsDate;
            },
            campEndsValid() {
                return this.campStartDate <= this.campEndDate;
            },
            weekDays: {
                get() {
                    return [
                        this.form.camp.sunday, this.form.camp.monday, this.form.camp.tuesday,
                        this.form.camp.wednesday, this.form.camp.thursday, this.form.camp.friday, this.form.camp.saturday
                    ];
                },
                set(value) {
                    this.setWeekDays(value);
                }
            },
            campStartDate: {
                get() {
                    return this.form.camp.campStartDate;
                },
                set(value) {
                    this.setCampStartDate(value);
                }
            },
            campEndDate: {
                get() {
                    return this.form.camp.campEndDate;
                },
                set(value) {
                    this.setCampEndDate(value);
                    if (!this.$route.params.id && !this.duplicate) {
                        setTimeout(() => {
                            this.signUpEndsDate = moment(value).subtract(1, 'day').toDate();
                        });
                    }
                }
            },
            noCampDates: {
                get() {
                    return this.form.camp.noCampDates;
                },
                set(value) {
                    this.setNoCampDates(value);
                }
            },
            signUpBeginsDate: {
                get() {
                    return this.form.camp.signUpBeginsDate;
                },
                set(value) {
                    this.setSignUpBeginsDate(value);
                }
            },
            signUpBeginsTime: {
                get() {
                    return this.form.camp.signUpBeginsTime;
                },
                set(value) {
                    this.setSignUpBeginsTime(value);
                }
            },
            signUpEndsDate: {
                get() {
                    return this.form.camp.signUpEndsDate;
                },
                set(value) {
                    this.setSignUpEndsDate(value);
                }
            },
            signUpEndsTime: {
                get() {
                    return this.form.camp.signUpEndsTime;
                },
                set(value) {
                    this.setSignUpEndsTime(value);
                }
            },
            selectedMoreThenOneDay() {
                return this.weekDays.filter(el => el).length;
            },
            campEndDateError() {
                if (this.campStartDate && this.campEndDate && !this.campEndsValid) {
                    return "The end date should be after start date";
                } else if ((!this.$route.params.id || this.isCampFormDraft) && this.campEndDate && this.futureDate(this.campEndDate)) {
                    return "Should be future date";
                }
                return null;
            }
        },
        methods: {
            ...mapMutations({
                setWeekDays: Mutations.CAMP_FORM_SET_WEEK_DAYS,
                setCampStartDate: Mutations.CAMP_FORM_SET_CAMP_START_DATE,
                setCampEndDate: Mutations.CAMP_FORM_SET_CAMP_END_DATE,
                setNoCampDates: Mutations.CAMP_FORM_SET_NO_CAMP_DATES,
                setSignUpBeginsDate: Mutations.CAMP_FORM_SET_SIGN_UP_BEGINS_DATE,
                setSignUpEndsDate: Mutations.CAMP_FORM_SET_SIGN_UP_ENDS_DATE,
                setSignUpBeginsTime: Mutations.CAMP_FORM_SET_SIGN_UP_BEGINS_TIME,
                setSignUpEndsTime: Mutations.CAMP_FORM_SET_SIGN_UP_ENDS_TIME,
                setValid: Mutations.CAMP_FORM_SET_FORM_VALID
            }),

            currentOrFutureDate(value) {
                return value && !moment(value).isSameOrAfter(moment(), 'days');
            },

            futureDate(value) {
                return value && !moment(value).isAfter(moment(), 'days');
            }
        }
    }
</script>

<style lang="scss" scoped>
    @import "../../../../assets/colors";

    .step {
        padding: 50px 92px;
        background: $White;
        width: 100%;
        box-shadow: 0 0 20px 0 rgba(176, 186, 197, 0.15);
        @media screen and (max-width: 1200px) {
            padding: 45px 50px 50px;
        }
        @media screen and (max-width: 991px) {
            padding: 40px 30px 45px;
        }
        @media screen and (max-width: 767px) {
            padding: 40px 20px;
        }
        /deep/ .form-group {
            margin-bottom: 20px;
        }

        h2 {
            margin-bottom: 30px;

            &:nth-of-type(2) {
                margin: 26px 0 32px;
            }
        }
        .day-picker {
            margin-bottom: 29px;
        }
    }

    .error {
        top: -21px;
        &.registration-deadline-error {
            top: 69px;
            background: white;
            z-index: 2;
        }
    }

    .right-error {
        right: 0;
    }

    .mt-20 {
        margin-top: 20px;
        @media screen and (max-width: 575px) {
            margin-top: 0;
        }
    }

    .sign-up-start-date,
    .sign-up-end-date {
        /deep/ .with-tooltip .tooltiptext {
            @media screen and (max-width: 568px) {
                right: -145px;
                left: auto;
            }
        }
    }

    .no-class-dates {
        /deep/ .with-tooltip .tooltiptext {
            @media screen and (max-width: 568px) {
                right: -195px;
                left: auto;
            }
        }
    }
</style>


// WEBPACK FOOTER //
// src/components/main/pages/camp-form/Step3.vue